import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { Path } from '../../services/data-tree';
import { BasicFilterExpression, DataOptions, DataResponse, DataService } from '../../services/data.service';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { att } from '../../services/measures';
import { createMeasureColumn, DataRow, getGroupAttributes } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { Axis } from '../../services/axis';
import { Sort } from '../../shared/components/table/table/table.component';
import { maxOver, noAgg0 } from '../../services/aggregation';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';

interface CijferkolommenI extends Attributes {
	ck_m_count_resultaatkolom: number;
}

interface CijferkolommenA extends Attributes {
	ck_m_count_resultaatkolom: number;
	max_count: number;
}

@Component({
	selector: 'app-cijferkolommen',
	templateUrl: './cijferkolommen.component.html',
	styleUrls: ['./cijferkolommen.component.scss'],
	imports: [DashboardContainerComponent, FilterPanelComponent, DashboardHeaderComponent, BarchartTableComponent, FilterBarComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CijferkolommenComponent extends BarchartTableConfig<CijferkolommenI, CijferkolommenA> {
	private readonly dataService = inject(DataService);

	selectedGroups: AttrPath[] = [['ck_abb_kolomkop']];

	override fixedBeforeGroups = 1;

	defaultFilters: FilterName[] = ['ck_nm_schooljaar', 'ck_nm_vestiging'];

	permanentFilterExpressions = [new BasicFilterExpression(['ck_is_any_laatste_gemiddelde'], 1)];

	factTable = Table.fac_ck_cijferkolommen;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijferkolommenData(options);
	}

	protected override singleAggregators = {
		ck_m_count_resultaatkolom: noAgg0('ck_m_count_resultaatkolom'),
		max_count: maxOver('ck_m_count_resultaatkolom'),
	};

	override getRowClickUrl(
		context: DashboardContext<CijferkolommenI, CijferkolommenA, CijferkolommenComponent>,
		path: Path<CijferkolommenA, number[]>
	): UrlTree | undefined {
		const { ck_abb_kolomkop } = getGroupAttributes(context, path);

		const params = this.filterService.convertToFilters([{ name: 'ck_abb_kolomkop', value: ck_abb_kolomkop }], {});
		return this.urlService.getRedirectUrlTree(['/beheer/cijferinstellingen/cijferkolommen-details'], params);
	}

	override createMeasureColumns(): ColumnDef<DataRow<CijferkolommenA>>[] {
		return [
			createMeasureColumn<Attributes, CijferkolommenA>(
				'Aantal kolommen',
				att<'ck_m_count_resultaatkolom', CijferkolommenA>('ck_m_count_resultaatkolom')
			),
		];
	}

	override createXAxis(context: DashboardContext<CijferkolommenI, CijferkolommenA, CijferkolommenComponent>): Axis {
		return { min: 0, max: context.dataRoot!.a.max_count, ticks: [] };
	}

	override getDefaultSort(): Sort {
		return { active: 'Aantal kolommen', direction: 'desc' };
	}

	override getBarchartQty(path: Path<CijferkolommenA, number[]>) {
		return att<'ck_m_count_resultaatkolom', CijferkolommenA>('ck_m_count_resultaatkolom')(path);
	}

	override enrichTableModel(
		_context: DashboardContext<CijferkolommenI, CijferkolommenA, CijferkolommenComponent>,
		tableModel: TableModel<DataRow<CijferkolommenA>>
	) {
		tableModel.showFooters = false;
	}
}
